import type { NetworkStatus, AdminNetworkTelemetry } from "@woven/contracts";
import { useCallback, type ReactNode } from "react";
import { useControlApiClient } from "../../lib/hooks";
import { usePublicNetworkTelemetry, useTelemetry } from "./networkTelemetryHooks";

export function TelemetrySummary({ data }: { data: NetworkStatus | null }) {
  return (
    <section className="panel" aria-label="Node telemetry">
      <h2 className="panel-title">Configured node telemetry</h2>
      <p role="status">Telemetry {data?.freshness ?? "unavailable"}</p>
      <p>
        One configured Woven node, not global network health. HTTP metrics availability does not
        verify transport operation.
      </p>
      <p>
        Last observed: {data?.observedAt ?? "Unavailable"} · Last attempted:{" "}
        {data?.lastAttemptAt ?? "Unavailable"}
      </p>
      <div className="metric-grid">
        <div className="metric-tile">
          <p className="metric-label">Active connections</p>
          <p className="metric-value">{data?.connectionsActive ?? "Unavailable"}</p>
        </div>
        <div className="metric-tile">
          <p className="metric-label">Provisioned sessions</p>
          <p className="metric-value">{data?.sessionsActive ?? "Unavailable"}</p>
        </div>
      </div>
      <p>Publish rate: Unavailable — rate calculation is not enabled.</p>
      {data?.freshness === "stale" && (
        <p>Last known observations only; the latest collection failed.</p>
      )}
    </section>
  );
}

export function PublicNetworkTelemetry({
  children,
}: {
  children?: (data: NetworkStatus | null) => ReactNode;
}) {
  const { data } = usePublicNetworkTelemetry();
  return children ? children(data) : <TelemetrySummary data={data} />;
}

export function AdminNetworkTelemetryPanel() {
  const client = useControlApiClient();
  const load = useCallback((signal: AbortSignal) => client.getNetworkTelemetry(signal), [client]);
  const { data, reload } = useTelemetry<AdminNetworkTelemetry>(load);
  return (
    <div>
      <TelemetrySummary data={data} />
      <section className="panel">
        <div className="dashboard-preview-head">
          <h2 className="panel-title">Admin observations</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={reload}>
            Retry telemetry
          </button>
        </div>
        <p>
          Collector: {data ? (data.enabled ? "enabled" : "disabled") : "unavailable"} · Last
          collection error: {data?.failure ?? "none reported"} · Lifetime tracking error:{" "}
          {data?.lifetimeFailure ?? "none reported"}
        </p>
        {data?.lifetimeTracking?.scope === "durable" && (
          <p>
            Durable observed lifetime totals survive node and API restarts. Tracking enabled:{" "}
            {data.lifetimeTracking.trackedSince} · Last saved observation:{" "}
            {data.lifetimeTracking.observedAt}. Unsampled activity before a restart can be missed.
          </p>
        )}
        <p>
          Max gauges are configured limits, not verified capacity. Counters are process-lifetime
          totals, not 24-hour activity. Events delivered counts recipient attempts, not confirmed
          delivery. Delivered payload bytes are fan-out accounting, not billable egress. Evictions
          include coalescing.
        </p>
        {data?.metrics ? (
          <dl>
            {Object.entries(data.metrics).map(([name, value]) => (
              <div key={name}>
                <dt>{name}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p>Detailed metrics unavailable.</p>
        )}
      </section>
    </div>
  );
}
